/* eslint-disable no-throw-literal */
import { useNavigate, useParams } from "react-router-dom";
import Image from "../../../components/ui/Image";
import { Hourglass, Paperclip, Pin } from "lucide-react";
import CalendarIcon from "../../../assets/icons/CalendarIcon.svg";
import MapPin from "../../../assets/icons/MapPin.svg";
import NoteBook from "../../../assets/icons/NoteBook.svg";
import IvoryLogo from "../../../assets/icons/IvoryLogo.svg";
import React, { useEffect, useState } from "react";
import { useUserSelection } from "../../../store/UserPayment";
import getToken from "../../../hooks/getToken";
import TrainingCourseImg from "../../../assets/used-images/defaultImg.jpeg";
import axios from "axios";
import { useMutation } from "@tanstack/react-query";
import { useLanguage } from "../../../store/language-context";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

const BoxContent = React.memo(({ course, selectedPayment, rowDetails, discountPercentage, page }) => {
    const { t } = useTranslation();

    return (
        <div className=" p-3 rounded-xl h-fit">
            <div className="bg-gray-200 grid grid-cols-[34%_64%] max-2xl:grid-cols-1 gap-2 p-2 rounded-xl">
                <div className="w-[8.5rem] h-[7.5rem] relative max-xl:w-full flex items-center justify-center max-xl:px-1">
                    <div className="bg-secondary-tw z-10 absolute w-full h-full rounded-md"></div>
                    <Image
                        alt={'course-title'}
                        src={course?.image || TrainingCourseImg}
                        className={'w-32 h-28 object-cover relative z-50 rounded-md max-xl:w-full'}
                    />
                </div>
                <div className="space-y-2">
                    {/* title */}
                    <div>
                        <p className="text-primary-tw !text-start text-lg">
                            {course?.title}
                        </p>
                    </div>

                    {/* subject.category.title */}
                    {
                        course?.subject?.category?.title &&
                        <div className='flex items-center gap-2'>
                            <span className="w-4">
                                <Pin fill='#7A7A7A' size={16} color='#7A7A7A' />
                            </span>
                            <p className=' text-semi-gray text-sm m-0'>
                                {course?.subject?.category?.title}
                            </p>
                        </div>
                    }

                    {/* subject.title */}
                    {
                        course?.subject?.title &&
                        <div className='flex items-center gap-2 *:m-0'>
                            <span className="w-4">
                                <img src={NoteBook} alt="calendar-icon" className="w-3" />
                            </span>
                            <p className=' text-semi-gray text-sm'>
                                {course?.subject?.title}
                            </p>
                        </div>
                    }

                    {/* location */}
                    {
                        (page === 'offers' ? rowDetails?.city?.title : selectedPayment?.location) &&
                        <div className='flex items-center gap-2 *:m-0'>
                            <span className="w-4">
                                <img src={MapPin} alt="calendar-icon" className="w-3" />
                            </span>
                            <p className=' text-semi-gray text-sm'>
                                {page === 'offers' ? rowDetails?.city?.title : selectedPayment.location}
                            </p>
                        </div>
                    }

                    {/* calendar & days */}
                    {
                        ((page === 'offers' && (rowDetails?.start_date || selectedPayment.startDate)) || (!page === 'offers' && selectedPayment.startDate)) &&
                        <div className='flex items-center gap-2'>
                            <span className="w-4">
                                <img src={CalendarIcon} alt="calendar-icon" className="w-4" />
                            </span>
                            <div className=' text-semi-gray text-sm flex items-center gap-1 *:m-0'>
                                <p>
                                    {t('training-courses.from')} {page === 'offers' ? (rowDetails?.start_date || selectedPayment.startDate) : selectedPayment.startDate}
                                </p>
                                <p>
                                    {t('training-courses.to')} {page === 'offers' ? (rowDetails?.end_date || selectedPayment.endDate) : selectedPayment.endDate}
                                </p>
                            </div>
                        </div>
                    }

                    {/* duration */}
                    {
                        (page === 'offers' ? (rowDetails?.duration || selectedPayment.duration) : selectedPayment.duration) &&
                        <div className='flex items-center gap-2 *:m-0'>
                            <span className="w-4">
                                <Hourglass fill='#7A7A7A' size={16} color='#7A7A7A' />
                            </span>
                            <p className=' text-semi-gray text-sm '>
                                <span>
                                    {t('training-courses.duration')}
                                </span>
                                <span className="!mx-1">
                                    {page === 'offers' ? (rowDetails?.duration || selectedPayment.duration) : selectedPayment.duration}
                                </span>
                                <span>
                                    {t('training-courses.days')}
                                </span>
                            </p>
                        </div>
                    }

                    <div className="!my-4 *:m-0 flex items-center !gap-2 *:bg-primary-tw *:text-white *:flex *:items-center *:!gap-2">
                        <del className='px-3 h-6 rounded-lg w-fit text-nowrap flex items-center !gap-2'>
                            <span>
                                {rowDetails?.thisRowPrice || ""}
                            </span>
                            <span>
                                {selectedPayment.currencyTitle && selectedPayment.currencyTitle}
                            </span>
                        </del>
                        <p className="px-4 h-6 !py-0 rounded-lg w-fit text-nowrap flex items-center !gap-2">
                            {
                                page === 'offers' ?
                                    <span>
                                        {rowDetails?.courseOfferPrice || ""}.00
                                    </span>
                                    :
                                    discountPercentage ?
                                        <>
                                            {Number(rowDetails?.thisRowPrice) - (Number(rowDetails?.thisRowPrice) * (Number(discountPercentage) / 100))}.00
                                        </>
                                        :
                                        rowDetails?.thisRowPrice
                            } {selectedPayment.currencyTitle && selectedPayment.currencyTitle}
                        </p>
                    </div>
                </div>
            </div>
        </div>
    )
});

const PaymentCard = ({ page, whatLinkToGetBox }) => {
    const { URL } = useLanguage();
    const { t } = useTranslation();
    const { selectedPayment, rowDetails, setRowDetails, setSelectedPayment } = useUserSelection();
    document.title = page === 'offers' ? `${t('routes.offers')} | ${t('routes.payment-card')}` : `${t('routes.training-courses')} | ${t('routes.payment-card')}`;

    const { id } = useParams();
    const navigate = useNavigate();
    const token = getToken();

    const { data: course } = useQuery({
        queryKey: ['payment-data', id, whatLinkToGetBox, URL],
        queryFn: async () => {
            const response = await axios.get(`${URL}/courses/${id}`);
            if (response.status !== 200) throw new Error(t('custom.err'));
            return response?.data?.data?.data ?? response?.data?.data;
        }
    });

    const [data, setData] = useState({
        couponCode: null,
        couponErr: null,
        discount: 0,
        discountPercentage: 0,
        imgUploaded: null,
        imgErr: null,
        cardLoading: false
    });

    const getPaymentLink = () => {
        if (page === "offers") {
            return `${URL}/offer/payment/${selectedPayment.payment}`;
        }

        if (selectedPayment.payment === "tabby" && !rowDetails.courseFeeId) {
            return `${URL}/tabby`;
        }

        if (rowDetails.courseFeeId) {
            return `${URL}/course/extra-fees/payment/${selectedPayment.payment}`;
        }

        return `${URL}/payment/${selectedPayment.payment}`;
    };

    const getRequestedBody = () => {
        if (page === "offers") {
            return {
                price: rowDetails?.courseOfferPrice,
                course_id: rowDetails?.courseId,
                course_offer_id: rowDetails?.courseOfferId
            };
        } else {
            if (rowDetails.courseFeeId && !rowDetails.courseRegistrationId) {
                return {
                    price: rowDetails.courseFeePrice,
                    course_fee_id: rowDetails.courseFeeId,
                    course_id: rowDetails?.courseId,
                };
            } else {
                return {
                    price: rowDetails?.thisRowPrice,
                    course_registrations_id: rowDetails?.courseRegistrationId,
                    coupon_code: data?.couponCode
                };
            }
        }
    };

    // coupon
    const couponMutation = useMutation({
        mutationFn: async (couponCode) => {
            try {
                const response = await axios.get(`${URL}/check-coupon?coupon_code=${couponCode}`, {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                });
                return response.data;
            } catch (error) {
                throw {
                    message: error.response?.data?.message || t('training-courses.coupon-notvalid'),
                    status: error.response?.status
                };
            }
        },
        onSuccess: (data) => {
            setData(prev => ({
                ...prev,
                discountPercentage: data?.coupon?.percentage || 0,
                couponErr: null
            }));
        },
        onError: (error) => {
            setData(prev => ({
                ...prev,
                couponErr: error.message || t('training-courses.coupon-notvalid'),
                discountPercentage: 0
            }));
        }
    });

    // bank transfer (لو بنك)
    const bankTransferMutation = useMutation({
        mutationFn: async (formData) => {
            try {

                const response = await axios.post(
                    rowDetails.courseFeeId
                        ? `${URL}/course/extra-fees/payment/bank-transfer`
                        : `${URL}/payment/bank-transfer`,
                    formData,
                    {
                        headers: {
                            Authorization: `Bearer ${token}`,
                            "Content-Type": "multipart/form-data"
                        }
                    }
                );
                return response.data;
            } catch (error) {
                throw {
                    message: error.response?.data?.message || t('training-courses.img-err'),
                    status: error.response?.status,
                    data: error.response?.data
                };
            }

        },
        onError: (error) => {
            setData(prev => ({
                ...prev,
                imgErr: error.message || t('training-courses.img-err')
            }));
        }
    });

    // payment (مش بنك)
    const paymentMutation = useMutation({
        mutationFn: async (paymentData) => {
            try {
                const response = await axios.post(
                    getPaymentLink(),
                    paymentData,
                    {
                        headers: {
                            Authorization: `Bearer ${token}`,
                            "Content-Type": "application/json"
                        }
                    }
                );
                return response.data;
            } catch (error) {
                throw {
                    message: error.response?.data?.message || t('training-courses.pay-err'),
                    status: error.response?.status,
                    data: error.response?.data
                };
            }
        },
        onError: () => {
            return;
        }
    });

    const isLoading = bankTransferMutation.isPending || paymentMutation.isPending || couponMutation.isPending;
    const error = bankTransferMutation.error || paymentMutation.error || couponMutation.error || data.couponErr;
    const resFromBank = bankTransferMutation.data || paymentMutation.data;

    const payFunc = async (e) => {
        e.preventDefault();
        setData(prev => ({ ...prev, cardLoading: true }));

        try {
            if (selectedPayment.payment === 'bank-transfer') {
                if (!data.imgUploaded) {
                    setData(prev => ({ ...prev, imgErr: t('training-courses.img-err'), cardLoading: false }));
                    return;
                }

                const formData = new FormData();
                formData.append('image', data.imgUploaded);
                formData.append("course_id", String(rowDetails.courseId));

                if (page === "offers") {
                    formData.append('price', rowDetails.courseOfferPrice);
                    formData.append('course_offer_id', rowDetails.courseOfferId);
                } else {
                    formData.append('price', rowDetails.thisRowPrice);
                    formData.append('course_registrations_id', rowDetails.courseRegistrationId);
                }

                if (rowDetails.courseFeeId) {
                    formData.append('course_fee_id', rowDetails.courseFeeId);
                    formData.append('price', rowDetails.courseFeePrice);
                }

                if (data.couponCode) {
                    try {
                        await couponMutation.mutateAsync(data.couponCode);
                        await bankTransferMutation.mutateAsync(formData);
                    } catch (error) {
                        return;
                    }
                } else {
                    // مفيش كوبون
                    await bankTransferMutation.mutateAsync(formData);
                }
            } else {
                // لو فيه كوبون
                if (data.couponCode) {
                    try {
                        await couponMutation.mutateAsync(data.couponCode);
                        if (!couponMutation.error) {
                            const requestBody = getRequestedBody();
                            await paymentMutation.mutateAsync(requestBody);
                        }
                    } catch (error) {
                        return;
                    }
                } else {
                    // مفيش كوبون
                    const requestBody = getRequestedBody();
                    await paymentMutation.mutateAsync(requestBody);
                }
            }
        } catch (error) {
            return;
        } finally {
            setData(prev => ({ ...prev, cardLoading: false }));
        }
    };

    useEffect(() => {
        if ((paymentMutation.isSuccess || bankTransferMutation.isSuccess) && !error) {

            if (selectedPayment.payment === 'bank-transfer') {
                setTimeout(() => { navigate(`/${page}`); }, 500);
            }

            else {
                setRowDetails({
                    courseId: '',
                    courseOfferId: '',
                    courseOfferPrice: '',

                    // training-courses
                    discount: '',
                    thisUserId: '',
                    thisCompanyId: '',
                    courseRegistrationId: '',
                    userToken: '',
                    thisRowPrice: '',
                    theImgUploaded: '',

                    // course fees
                    courseFeeId: '',
                    courseFeePrice: ''
                });
                setSelectedPayment({
                    location: '', // where's course place ex: (الرياض / السعوديه)
                    currencyTitle: '', // currency title (SAR || ريال سعودي)
                    date: '', // date of course
                    duration: '', // duration in days
                    payment: '', // payment selection
                    paymentInAr: '',
                });
                window.open(resFromBank?.data?.InvoiceURL, '_blank');
                setTimeout(() => {
                    navigate(`/training-courses/${id}`);
                    window.location.reload();
                }, 500);
            }
        }
    }, [paymentMutation.isSuccess, bankTransferMutation.isSuccess, error, selectedPayment.payment]);

    // for prevent reload
    useEffect(() => {
        const toPreventRefresh = (e) => {
            if ((paymentMutation.isSuccess || bankTransferMutation.isSuccess) && !error) {
                navigate(`/${page}`)
            } else {
                const confirmRefresh = window.confirm(
                    t('training-courses.lose-data')
                );
                if (confirmRefresh) {
                    navigate(`/${page}`);
                } else {
                    e.preventDefault();
                }
            }
        };
        window.addEventListener('beforeunload', toPreventRefresh);
        return () => {
            window.removeEventListener('beforeunload', toPreventRefresh);
        };
    }, [navigate, paymentMutation.isSuccess, bankTransferMutation.isSuccess, error]);

    useEffect(() => {
        if (!rowDetails.courseId &&
            (page === "training-courses" &&
                (!rowDetails.courseRegistrationId && !rowDetails.courseFeeId))
        ) {
            navigate(`/${page}/${id}`);
        }
    }, [rowDetails, page]);

    const getErrorMessage = () => {
        if (bankTransferMutation.error) {
            return bankTransferMutation.error.message || t('training-courses.bank-err');
        }
        if (paymentMutation.error) {
            return paymentMutation.error.message || t('training-courses.pay-err');
        }
        if (couponMutation.error && data.couponCode) {
            return couponMutation.error.message || t('training-courses.coupon-notvalid');
        }
        return null;
    };

    return (
        <section className="grid grid-cols-2 gap-2 max-lg:grid-cols-1 border rounded-xl">

            <BoxContent course={course} selectedPayment={selectedPayment} rowDetails={rowDetails} page={page} discountPercentage={data.discountPercentage} />

            {/* CARD DATA */}
            <div className="p-3 rtl:rounded-l-xl ltr:rounded-r-xl lg:ltr:border-l-2 lg:rtl:border-r-2">
                {/* SELECTED PAYMENT METHOD */}
                <div className="flex items-center gap-2 mb-3">
                    <div>
                        <img src={IvoryLogo} alt="ivory-logo" className="w-6 mb-1" />
                    </div>
                    <h1 className="!text-start text-xl">
                        {selectedPayment?.paymentInAr}
                    </h1>
                </div>
                <div className="flex items-center justify-between *:m-0">
                    <p className="text-base">{t('training-courses.product-price')}</p>
                    <p className="text-base">
                        {page === 'offers' ? 
                            rowDetails?.thisRowPrice || rowDetails?.courseFeePrice || "0"
                            : 
                            rowDetails?.thisRowPrice || rowDetails?.courseFeePrice || "0"
                        }
                        {" "}
                        
                    </p>
                </div>
                <div className="flex items-center justify-between my-1 *:m-0">
                    <p className="text-base">{t('training-courses.discount')}</p>
                    <p className="text-base">
                        % {page === 'offers' ? rowDetails?.discount || "0" : data.discountPercentage || "0"}
                    </p>
                </div>
                {/* عرض قيمة الخصم أسفل الخصم */}
                <div className="flex items-center justify-between my-1 *:m-0">
                    <p className="text-base">{t('training-courses.discount-value')}</p>
                    <p className="text-base">
                        {page === 'offers' ?
                            // For offers page
                            (rowDetails?.thisRowPrice || rowDetails?.courseFeePrice) ?
                                `${Math.abs(Number(rowDetails?.thisRowPrice || rowDetails?.courseFeePrice) * (Number(rowDetails?.discount || 0) / 100)).toFixed(2)}`
                                : "0.00"
                            :
                            // For regular courses with coupon
                            (rowDetails?.thisRowPrice || rowDetails?.courseFeePrice) && data.discountPercentage > 0 ?
                                `${(Number(rowDetails?.thisRowPrice || rowDetails?.courseFeePrice) * (Number(data.discountPercentage) / 100)).toFixed(2)}`
                                : "0.00"
                        } 
                    </p>
                </div>
                <div className="border-[1px] border-dashed border-b border-[#D9D9D9]"></div>
                <div className="flex items-center justify-between my-2">
                    <p className="text-base">{t('training-courses.total')}</p>
                   
                    <p className="text-base">
                        {
                            page === 'offers' ?
                                rowDetails?.courseOfferPrice || ""
                                :
                                data.discountPercentage ?
                                    <>
                                        {Number(rowDetails?.thisRowPrice) - (Number(rowDetails?.thisRowPrice) * (Number(data.discountPercentage) / 100))}.00 -Number(rowDetails?.thisRowPrice)
                                    </>
                                    :
                                    rowDetails?.thisRowPrice
                        } 
                    </p>
                </div>

                <div className="border-[1px] border-dashed border-b mb-4 border-[#D9D9D9]"></div>

                <form action="POST" className="space-y-3" onSubmit={payFunc}>
                    {selectedPayment.payment === 'bank-transfer' &&
                        <div>
                            <label htmlFor="card-img" className="items-center gap-2 flex">
                                <span>
                                    <img src={IvoryLogo} alt="ivory-logo" />
                                </span>
                                <span>{t('training-courses.invoice')}</span>
                            </label>
                            <div className="relative">
                                <input
                                    type="file"
                                    accept="image/*"
                                    id="card-img"
                                    className={`border rounded-md p-2 w-full mt-2 bg-[#F5F5F5] focus:ring-1 focus:ring-[#E5E5E5] ${bankTransferMutation.error || data.imgErr ? 'ring-1 ring-red-500' : ''}`}
                                    onChange={(e) => setData((prev) => ({
                                        ...prev,
                                        imgUploaded: e.target.files[0],
                                        imgErr: null
                                    }))}
                                    required
                                />
                                <label htmlFor="card-img" className="absolute end-1 top-2 rtl:rounded-l-lg ltr:rounded-r-lg justify-center items-center bg-primary-tw h-12 w-16 cursor-pointer flex">
                                    <Paperclip size={20} color="white" />
                                </label>
                            </div>
                            {data.imgErr &&
                                <span className="text-sm text-red-800 !text-start block">
                                    {data?.imgErr}
                                </span>
                            }
                        </div>
                    }
                    {
                        page !== "offers" &&
                        <div>
                            <label htmlFor="coupon-code" className="items-center gap-2 flex">
                                <span>
                                    <img src={IvoryLogo} alt="ivory-logo" />
                                </span>
                                <span>{t('training-courses.coupon-code')}</span>
                            </label>
                            <input
                                type="text"
                                id="coupon-code"
                                className={`border rounded-md p-2 w-full mt-2 bg-[#F5F5F5] focus:ring-1 focus:ring-[#E5E5E5] ${data.couponErr ? 'ring-1 ring-red-600' : ''}`}
                                onChange={(e) => setData(prev => ({ ...prev, couponCode: e.target.value, couponErr: null }))}
                            />
                            {couponMutation.isPending &&
                                <span className="text-sm text-secondary-tw !text-start block">
                                    {t('training-courses.see-coupon')}...
                                </span>
                            }
                        </div>
                    }
                    <div>
                        {/* {
                            (resFromBank?.data?.InvoiceURL && !error) &&
                            <Link to={resFromBank?.data?.InvoiceURL} className="pay-link">
                                {t('training-courses.continue-paying')}
                            </Link>
                        } */}
                        {
                            (resFromBank?.data?.payment_type === 'bank_transfer' && !data.couponErr && !error && !isLoading) &&
                            <div>
                                <p className="text-primary-tw text-base">
                                    {t('training-courses.paying-done')}.
                                </p>
                            </div>
                        }
                    </div>
                    <button
                        type="submit"
                        disabled={isLoading}
                        className="w-full py-2 bg-primary-tw text-white my-2 rounded-lg disabled:bg-primary-tw/70">
                        {isLoading ?
                            <div className="flex justify-center items-center gap-2">
                                <span>{t('custom.checking')}</span>
                                <div className="size-5 border-2 border-gray-200 rounded-full animate-spin-slow border-t-transparent"></div>
                            </div>
                            :
                            (resFromBank?.InvoiceURL && !data.couponErr && !data.couponCode)
                                ?
                                t('custom.done-check')
                                :
                                t('custom.check')
                        }
                    </button>
                    {getErrorMessage() && (
                        <p className="text-red-600 text-sm !text-start p-2 bg-red-50 rounded-md border border-red-200">
                            {getErrorMessage()}
                        </p>
                    )}
                    {(paymentMutation.data?.InvoiceURL || bankTransferMutation.data?.InvoiceURL) && (
                        <p className="text-green-600 text-sm !text-start p-2 bg-green-50 rounded-md border border-green-200">
                            {t('training-courses.paying-done')}.
                        </p>
                    )}
                </form>
            </div>
        </section>
    );
};


export default PaymentCard;